import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Anchor,
    Avatar,
    Box,
    Button,
    Heading,
    Image,
    Layer,
    Page,
    PageContent,
    Text,
    Tip,
} from 'grommet';
import { useLoaderData, useParams, useRevalidator } from 'react-router-dom';
import {
    API,
    openInNewTab,
    NewMatchButton,
    Utils,
    MessageButton,
} from '@enrich/shared-lib';
import { CopyButton } from '../../components/CopyButton';
import { ProgramActions } from './ProgramActions';
import { ResumeActions } from './ResumeActions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGraduationCap } from '@fortawesome/free-solid-svg-icons';
import {
    Book,
    Certificate,
    Clear,
    Close,
    DocumentVerified,
    Edit,
    Globe,
    Group,
    Halt,
    Home,
    InProgress,
    Language,
    Linkedin,
    MailOption,
    Organization,
    Phone,
    Run,
    User,
    UserExpert,
    UserManager,
    UserNew,
} from 'grommet-icons';
import { CandidacyTable } from './CandidacyTable';
import { ContactDocuments } from './ContactDocuments';
import { ContactNotes } from './ContactNotes';
import { InactiveStatus } from './InactiveStatus';
import { ProgramStatusButton } from './ProgramStatusButton';
import { format } from 'date-fns';
import { ContactProfileFormModalButton } from './ContactProfileFormModalButton';
import { ContactMessages } from './ContactMessages';
import { ContactMeetings } from './ContactMeetings';
import { ReverseMatchButton } from './ReverseMatchButton';
import { ContactEvents } from './ContactEvents';
import { ResumeReviewerDisplay } from './ContactProfileSupplement';

export const ContactProfile = ({
    showAdminActions = true,
    showMatchActions = showAdminActions,
    showResumeActions = showAdminActions,
    clientCompanyName = null,
    hideRaceData = false,
}) => {
    const loaderData = useLoaderData();
    const contact = loaderData;
    const revalidator = useRevalidator();
    const { companySlug, jobId } = useParams();
    const candidacies = contact.attributes.candidacies?.data;
    const coach = contact.attributes.coach?.data;
    const resumeReviewer = contact.attributes.resumeReviewer?.data;
    const avatarUrl = contact.attributes.avatar?.data?.attributes?.url ?? null;
    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const fullName =
        `${contact.attributes.firstName} ` + `${contact.attributes.lastName}`;
    const documents = contact.attributes.documents?.data || [];
    const ethnicities =
        contact.attributes.ethnicities?.data
            .map((ethnicity) => ethnicity.attributes.name)
            .join(', ') || 'N/A';
    const experiences =
        contact.attributes.experiences?.data
            .map((experience) => experience.attributes.name)
            .join(', ') || 'N/A';
    const gender = formatGender(contact.attributes.gender);
    const languages =
        contact.attributes.languages?.data
            .map((language) => language.attributes.name)
            .join(', ') || 'N/A';
    const majors =
        contact.attributes.majors?.data
            .map((major) => major.attributes.name)
            .join(', ') || 'N/A';
    const preferredIndustries =
        contact.attributes.preferredIndustries?.data
            .map((industry) => industry.attributes.name)
            .join(', ') || 'N/A';
    const preferredJobFunctions =
        contact.attributes.preferredJobFunctions?.data
            .map((jobFunction) => jobFunction.attributes.name)
            .join(', ') || 'N/A';
    const relocationPreferences =
        contact.attributes.preferredLocations?.data
            .map((location) => location.attributes.name)
            .join(', ') || 'N/A';
    const technicalSkills =
        contact.attributes.technicalSkills?.data
            .map((skill) => skill.attributes.name)
            .join(', ') || 'N/A';
    const resume = documents.find(
        (document) =>
            document.attributes.document_type?.data?.attributes?.title ==
            'Resume',
    );
    const resumeLink = resume?.attributes?.link || undefined;
    const resumeContent = resume?.attributes?.content || undefined;
    const school = contact.attributes.school?.data?.attributes.name || 'N/A';
    const formattedDegreeType = formatDegreeType(contact.attributes.degreeType);
    const formattedGraduationSemester = formatGraduationSemester(
        contact.attributes.graduationSemester,
    );
    const user = contact.attributes.user?.data?.attributes;
    const meetings = contact.attributes.meetings.data.map((x) => ({
        id: x.id,
        ...x.attributes,
    }));
    const events = Utils.deepFlatten(contact.attributes.events.data);

    return (
        <Page
            kind="full"
            gap="small"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="small">
                <Box gap="small">
                    {showAdminActions && (
                        <ProgramActions
                            contact={contact}
                            revalidate={revalidator.revalidate}
                        />
                    )}
                    <ResumeActions
                        contact={contact}
                        resumeLink={resumeLink}
                        resumeContent={resumeContent}
                        revalidate={revalidator.revalidate}
                        showAdminActions={showResumeActions}
                    />
                    <InactiveStatus contact={contact} />
                </Box>

                <Box
                    direction="row-responsive"
                    gap="small"
                    align="center"
                    justify="between">
                    <Box direction="row" gap="small" align="center">
                        {avatarUrl ? (
                            <Tip content="Click to enlarge">
                                <Button
                                    plain
                                    a11yTitle={fullName}
                                    onClick={() => setShowAvatarModal(true)}>
                                    <Avatar
                                        size="large"
                                        src={avatarUrl}
                                        alt={fullName}
                                    />
                                </Button>
                            </Tip>
                        ) : (
                            <Avatar size="large" background="light-4">
                                <Text size="large" weight="bold">
                                    {contact.attributes.firstName?.[0]}
                                    {contact.attributes.lastName?.[0]}
                                </Text>
                            </Avatar>
                        )}
                        {showAvatarModal && avatarUrl && (
                            <Layer
                                position="center"
                                onEsc={() => setShowAvatarModal(false)}
                                onClickOutside={() =>
                                    setShowAvatarModal(false)
                                }>
                                <Box
                                    pad="medium"
                                    gap="small"
                                    align="center"
                                    data-testid="avatar-modal">
                                    <Box
                                        direction="row"
                                        justify="end"
                                        fill="horizontal">
                                        <Button
                                            icon={<Close />}
                                            a11yTitle="Close photo"
                                            onClick={() =>
                                                setShowAvatarModal(false)
                                            }
                                        />
                                    </Box>
                                    <Box
                                        width={{ max: '80vw' }}
                                        height={{ max: '80vh' }}>
                                        <Image
                                            src={avatarUrl}
                                            alt={fullName}
                                            fit="contain"
                                        />
                                    </Box>
                                    <Text weight="bold">{fullName}</Text>
                                </Box>
                            </Layer>
                        )}
                        <Heading level="2">
                            {contact.attributes.firstName}{' '}
                            {contact.attributes.lastName}
                        </Heading>
                        {contact.attributes.pronouns != null && (
                            <Heading level="2" color="enrich-green">
                                ({contact.attributes.pronouns})
                            </Heading>
                        )}
                        {contact.attributes.enrichProgramStatus ==
                            'waitlisted' && (
                            <Tip content="Waitlisted for program">
                                <InProgress color="black" size="medium" />
                            </Tip>
                        )}
                        {contact.attributes.enrichProgramStatus == 'denied' && (
                            <Tip content="Denied for program">
                                <Clear color="black" size="medium" />
                            </Tip>
                        )}
                        {contact.attributes.enrichProgramStatus ==
                            'offboarded' && (
                            <Tip content="Offboarded">
                                <Halt color="black" size="medium" />
                            </Tip>
                        )}
                        {contact.attributes.isApproved && (
                            <Tip content="Accepted to program">
                                <UserExpert color="black" size="medium" />
                            </Tip>
                        )}
                        {contact.attributes.isResumeReady && (
                            <Tip content="Resume approved">
                                <DocumentVerified
                                    color="black"
                                    size="medium"
                                    cursor={resumeLink ? 'pointer' : 'default'}
                                    onClick={
                                        resumeLink
                                            ? () => openInNewTab(resumeLink)
                                            : undefined
                                    }
                                />
                            </Tip>
                        )}
                        {showAdminActions && (
                            <ContactProfileFormModalButton contact={contact} />
                        )}
                    </Box>

                    {(showAdminActions ||
                        showMatchActions ||
                        clientCompanyName) && (
                        <Box direction="row-responsive" gap="small">
                            {showAdminActions && (
                                <MessageButton contact={contact} />
                            )}
                            {clientCompanyName && (
                                <MessageButton
                                    contact={contact}
                                    fromCompany={clientCompanyName}
                                />
                            )}
                            {showAdminActions && (
                                <ProgramStatusButton
                                    contactId={contact.id}
                                    currentStatus={
                                        contact.attributes.enrichProgramStatus
                                    }
                                    revalidate={revalidator.revalidate}
                                />
                            )}
                            {showAdminActions && (
                                <NewMatchButton
                                    contactIds={[contact.id]}
                                    revalidate={revalidator.revalidate}
                                />
                            )}
                            {showMatchActions && (
                                <ReverseMatchButton contactId={contact.id} />
                            )}
                        </Box>
                    )}
                </Box>

                <Box direction="row-responsive" gap="small" fill="horizontal">
                    <Box
                        gap="small"
                        round="6px"
                        pad="small"
                        border={{ size: '2px' }}
                        fill="horizontal"
                        basis="medium">
                        <Box
                            align="center"
                            direction="row"
                            gap="small"
                            justify="start">
                            <FontAwesomeIcon icon={faGraduationCap} />
                            <Box justify="center">
                                <Text>
                                    {formattedGraduationSemester}{' '}
                                    {contact.attributes.graduationYear}
                                </Text>
                            </Box>
                        </Box>
                        <Box direction="row" gap="small" justify="start">
                            <Edit color="black" />
                            <Box justify="center">
                                <Text>
                                    {contact.attributes.gpa || 'N/A'}
                                    {contact.attributes.gpa && ' GPA'}
                                </Text>
                            </Box>
                        </Box>
                        <Box direction="row" gap="small" justify="start">
                            <Home color="black" />
                            <Box justify="center">
                                <Text>
                                    {contact.attributes.hometown || 'N/A'}
                                </Text>
                            </Box>
                        </Box>
                        <Box direction="row" gap="small" justify="start">
                            <Organization color="black" />
                            <Box justify="center">
                                <Text>{school || 'N/A'}</Text>
                            </Box>
                        </Box>
                        <Box direction="row" gap="small" justify="start">
                            <Book color="black" />
                            <Box justify="center">
                                <Text>{majors || 'N/A'}</Text>
                            </Box>
                        </Box>
                        <Box direction="row" gap="small" justify="start">
                            <Certificate color="black" />
                            <Box justify="center">
                                <Text>{formattedDegreeType}</Text>
                            </Box>
                        </Box>
                        <Box direction="row" gap="small" justify="start">
                            <Tip content="gender">
                                <User color="black" />
                            </Tip>
                            <Box justify="center">
                                <Text>{gender}</Text>
                            </Box>
                        </Box>
                        {!hideRaceData && (
                            <Box direction="row" gap="small" justify="start">
                                <Group color="black" />
                                <Box justify="center">
                                    <Text>{ethnicities}</Text>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Box
                        gap="small"
                        round="6px"
                        pad="small"
                        border={{ size: '2px' }}
                        fill="horizontal"
                        basis="large">
                        <Box border="between" gap="small">
                            <Box gap="xsmall" justify="start">
                                <Text weight="bold">Preferred Industries</Text>
                                <Box justify="center">
                                    <Text>{preferredIndustries}</Text>
                                </Box>
                            </Box>
                            <Box gap="xsmall" justify="start">
                                <Text weight="bold">
                                    Preferred Job Functions
                                </Text>
                                <Box justify="center">
                                    <Text>{preferredJobFunctions}</Text>
                                </Box>
                            </Box>
                            <Box gap="xsmall" justify="start">
                                <Text weight="bold">Experiences</Text>
                                <Box justify="center">
                                    <Text>{experiences}</Text>
                                </Box>
                            </Box>
                            <Box gap="xsmall" justify="start">
                                <Text weight="bold">Technical Skills</Text>
                                <Box justify="center">
                                    <Text>{technicalSkills}</Text>
                                </Box>
                            </Box>
                            <Box gap="xsmall" justify="start">
                                <Text weight="bold">
                                    Relocation Preferences
                                </Text>
                                <Box justify="center">
                                    <Text>{relocationPreferences}</Text>
                                </Box>
                            </Box>
                            <Box
                                direction="row-responsive"
                                gap="xsmall"
                                justify="start">
                                <Text weight="bold">Communication Level:</Text>
                                <Box justify="center">
                                    <Text>
                                        {
                                            // eslint-disable-next-line max-len
                                            Utils.resolveContactCommunicationLevelDescription(
                                                contact.attributes
                                                    .communicationLevel,
                                            )
                                        }
                                    </Text>
                                </Box>
                            </Box>
                            <Box gap="small">
                                <Box
                                    direction="row"
                                    gap="small"
                                    justify="start">
                                    <Language color="black" />
                                    <Box justify="center">
                                        <Text>{languages}</Text>
                                    </Box>
                                </Box>
                                {contact.attributes
                                    .isFirstGenerationCollegeStudent && (
                                    <Box
                                        direction="row"
                                        gap="small"
                                        justify="start">
                                        <UserNew color="black" />
                                        <Box justify="center">
                                            <Text>
                                                First Generation College Student
                                            </Text>
                                        </Box>
                                    </Box>
                                )}
                                {contact.attributes.isVarsityAthlete && (
                                    <Box
                                        direction="row"
                                        gap="small"
                                        justify="start">
                                        <Run color="black" />
                                        <Box justify="center">
                                            <Text>Varsity Athlete</Text>
                                        </Box>
                                    </Box>
                                )}
                                {contact.attributes.isVisaRequiredToWork && (
                                    <Box
                                        direction="row"
                                        gap="small"
                                        justify="start">
                                        <Globe color="black" />
                                        <Box justify="center">
                                            <Text>Work Visa Required</Text>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        gap="small"
                        round="6px"
                        pad="small"
                        border={{ size: '2px' }}
                        fill="horizontal"
                        basis="medium">
                        {coach && (
                            <Box direction="row" gap="small" justify="start">
                                <Tip content="Coach">
                                    <UserManager color="black" />
                                </Tip>
                                <Box justify="center">
                                    <Text>
                                        {coach.attributes.fullName || 'None'}
                                    </Text>
                                </Box>
                            </Box>
                        )}
                        {resumeReviewer && (
                            <ResumeReviewerDisplay
                                resumeReviewer={resumeReviewer}
                            />
                        )}
                        <Box
                            direction="row"
                            gap="small"
                            justify="start"
                            align="center">
                            <MailOption color="black" />
                            <Box justify="center">
                                <Text>{contact.attributes.email}</Text>
                            </Box>
                            <CopyButton
                                value={contact.attributes.email}
                                a11yTitle="Copy email address"
                            />
                        </Box>
                        <Box direction="row" gap="small" justify="start">
                            <Phone color="black" />
                            <Box justify="center">
                                <Text>
                                    {contact.attributes.phoneNumber || 'N/A'}
                                </Text>
                            </Box>
                        </Box>
                        <Box
                            direction="row"
                            gap="small"
                            justify="start"
                            align="center">
                            <Linkedin color="enrich-blue" />
                            {contact.attributes.linkedinUrl ? (
                                <Box
                                    direction="row"
                                    gap="xsmall"
                                    align="center">
                                    <Anchor
                                        label="LinkedIn Profile"
                                        onClick={() =>
                                            openInNewTab(
                                                contact.attributes.linkedinUrl,
                                            )
                                        }
                                    />
                                    <CopyButton
                                        value={contact.attributes.linkedinUrl}
                                        a11yTitle="Copy LinkedIn URL"
                                    />
                                </Box>
                            ) : (
                                <Box justify="center">
                                    <Text>N/A</Text>
                                </Box>
                            )}
                        </Box>

                        <Box direction="row" gap="small" justify="start">
                            <Text weight="bold">Last Login</Text>
                            <Box justify="center">
                                {user?.lastLogin ? (
                                    <Text>
                                        {format(
                                            user?.lastLogin,
                                            'dd MMM, yyyy h:mm aaa',
                                        )}
                                    </Text>
                                ) : (
                                    <Text>N/A</Text>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </PageContent>

            <PageContent>
                <ContactMeetings meetings={meetings} />
            </PageContent>

            {(showAdminActions || clientCompanyName) && (
                <ContactMessages
                    contactId={contact.id}
                    showConversationsLink={showAdminActions}
                />
            )}

            <PageContent>
                <Heading level="3">Documents</Heading>
                <ContactDocuments
                    key={contact.id}
                    documents={documents}
                    contact={contact}
                />
            </PageContent>

            <PageContent>
                <Heading level="3">Job Matches</Heading>
                <CandidacyTable
                    candidacies={candidacies}
                    companySlug={companySlug}
                />
            </PageContent>

            {showAdminActions && (
                <ContactNotes
                    contactId={contact.id}
                    notes={contact.attributes.contact_notes?.data}
                    revalidate={revalidator.revalidate}
                    showAdminActions={showAdminActions}
                />
            )}

            {/* <PageContent>
                <ContactEvents
                    contact={Utils.deepFlatten(contact)}
                    events={events}
                    companySlug={companySlug}
                    jobId={jobId}
                    showAdminActions={showAdminActions}
                />
            </PageContent> */}
        </Page>
    );
};

ContactProfile.propTypes = {
    showAdminActions: PropTypes.bool,
    showMatchActions: PropTypes.bool,
    showResumeActions: PropTypes.bool,
    clientCompanyName: PropTypes.string,
    hideRaceData: PropTypes.bool,
};

const formatDegreeType = (type) => {
    if (!type) return 'N/A';
    try {
        const degreeType = type.replace('_s', "'s").replace('_', ' ');
        return degreeType[0].toUpperCase() + degreeType.slice(1);
    } catch (error) {
        return type;
    }
};

const formatGender = (gender) => {
    if (!gender) return 'N/A';

    return gender[0].toUpperCase() + gender.slice(1);
};

const formatGraduationSemester = (semester) => {
    if (!semester) return 'N/A';

    return semester[0].toUpperCase() + semester.slice(1);
};

export async function loader({ params }) {
    const currentDate = Date.now();
    const startOfDay = new Date(currentDate);
    startOfDay.setHours(0, 0, 0, 0);
    const startOfDayISO = startOfDay.toISOString();

    const contactQuery = `query getContact($contactId: ID!) {
        contact(id: $contactId) {
            data {
                id
                attributes {
                    degreeType
                    email
                    enrichProgramStatus
                    firstName
                    gender
                    gpa
                    graduationSemester
                    graduationYear
                    hometown
                    hasAgreedCodeOfConduct
                    isAmbassador
                    isApproved
                    isFirstGenerationCollegeStudent
                    isOnboardingCompleted
                    isResumeReady
                    isVarsityAthlete
                    isVisaRequiredToWork
                    lastName
                    linkedinUrl
                    onboardingStage
                    phoneNumber
                    pronouns
                    studentDebtLevel
                    resumeStatus
                    communicationLevel

                    avatar {
                        data {
                            attributes {
                                url
                            }
                        }
                    }

                    candidacies {
                        data {
                            id
                            attributes {
                                status
                                job {
                                    data {
                                        id
                                        attributes {
                                            title
                                            company {
                                                data {
                                                    attributes {
                                                        name
                                                        slug
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                updatedAt
                            }
                        }
                    }
                    
                    coach {
                        data {
                            attributes {
                                fullName
                            }
                        }
                    }

                    contact_notes (sort: "createdAt:desc") {
                        data {
                            id
                            attributes {
                                createdAt
                                message
                                subject
                            }
                        }
                    }

                    documents {
                        data {
                            id
                            attributes {
                                content
                                link

                                document_type {
                                    data {
                                        attributes {
                                            title
                                        }
                                    }
                                }
                            }
                        }
                    }

                    experiences {
                        data {
                            attributes {
                                name
                            }
                        }
                    }

                    ethnicities {
                        data {
                            attributes {
                                name
                            }
                        }
                    }

                    languages {
                        data {
                            attributes {
                                name
                            }
                        }
                    }

                    majors { 
                        data {
                            attributes {
                                name
                            }
                        }
                    }

                    preferredIndustries {
                        data {
                            attributes {
                                name
                            }
                        }
                    }

                    preferredJobFunctions {
                        data {
                            attributes {
                                name
                            }
                        }
                    }

                    school {
                        data { 
                            attributes { 
                                name
                            }
                        }
                    }

                    technicalSkills {
                        data {
                            attributes {
                                name
                            }
                        }
                    }

                    preferredLocations {
                        data {
                            id
                            attributes {
                                name
                            }
                        }
                    }

                    user {
                        data {
                            id
                            attributes {
                                lastLogin
                            }
                        }
                    }

                    meetings(
                      filters: {
                        and: [
                          {meetingTime: {notNull: true }},
                          {meetingTime: {gte: "${startOfDayISO}"}}
                        ]},
                      sort: ["meetingTime:asc"]) {
                        data {
                            id
                            attributes {
                                meetingTime
                                meetingReason
                                joinUrl
                            }
                        }
                    }

                    resumeReviewer {
                        data {
                            id
                            attributes {
                                user {
                                    data {
                                        id
                                        attributes {
                                            firstName
                                            lastName
                                        }
                                    }
                                }
                            }
                        }
                    }

                    events (filters: {type: {nei: "portal_login"}}, sort: ["createdAt:desc"]) {
                        data {
                            id
                            attributes {
                                type
                                createdAt
                                jsonData
                                source_user {
                                    data {
                                        id
                                        attributes {
                                            firstName
                                            lastName
                                            email
                                        }
                                    }
                                }
                                job {
                                    data {
                                        id
                                        attributes {
                                            title
                                            company {
                                                data {
                                                    id
                                                    attributes {
                                                        name
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                document {
                                    data {
                                        id
                                        attributes {
                                            link
                                            document_type {
                                                data {
                                                    id
                                                    attributes {
                                                        title
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                candidacy {
                                    data {
                                        id
                                        attributes {
                                            status
                                            rejectionReason
                                            job_locations {
                                                data {
                                                    attributes {
                                                        officeName
                                                        mergeLocation
                                                        location {
                                                            data {
                                                                attributes {
                                                                    name
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                                meeting {
                                    data {
                                        id
                                        attributes {
                                            meetingTime
                                            meetingReason
                                            joinUrl
                                            createdAt
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }`;

    try {
        const contactResponse = await API.post('graphql', {
            query: contactQuery,
            variables: { contactId: params.contactId },
        });
        return contactResponse.data.data.contact.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}
